import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../services/users.service';

interface FavoriteQuestion {
    id: string;
    title: string;
    interactionId?: string;
    icon?: string;
}

interface UpdateFavoriteQuestionsParams {
    userId: string;
    favoriteQuestions: FavoriteQuestion[];
}

export function useUpdateFavoriteQuestions() {
    return useMutation({
        mutationFn: async ({ userId, favoriteQuestions }: UpdateFavoriteQuestionsParams) => {
            const settingsKey = `mindy:favorite_questions`;
            const settings = {
                [settingsKey]: favoriteQuestions,
            };

            return usersApi.updateUserSelfOrganizationSettings(userId, settings);
        },
    });
}

export function useToggleFavoriteQuestion() {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();
    const updateFavoriteQuestionsMutation = useUpdateFavoriteQuestions();

    return useMutation({
        mutationFn: async ({
            questionText,
            userId,
            organizationId,
            interactionId,
        }: {
            questionText: string;
            userId: string;
            organizationId: string;
            interactionId?: string;
        }) => {
            const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

            if (!userInfo) {
                throw new Error('User info not available');
            }

            if (!interactionId) {
                throw new Error('Cannot favorite message without real interaction ID from backend');
            }

            const userSettings = userInfo.settings || {};
            const currentSettings = (userSettings[organizationId] as Record<string, unknown>) || {};
            const currentFavorites = (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

            const existingIndex = interactionId
                ? currentFavorites.findIndex((fav) => fav.interactionId === interactionId)
                : currentFavorites.findIndex((fav) => fav.title === questionText);

            let updatedFavorites: FavoriteQuestion[];
            let isAdding = false;

            if (existingIndex !== -1) {
                updatedFavorites = currentFavorites.filter((_, index) => index !== existingIndex);
                isAdding = false;
            } else {
                const newFavorite: FavoriteQuestion = {
                    id: interactionId as string,
                    title: questionText,
                    interactionId: interactionId as string,
                };

                updatedFavorites = [newFavorite, ...currentFavorites].slice(0, 4);
                isAdding = true;

                if (currentFavorites.length >= 4) {
                    toast({
                        description: t('chat.favorites.maxReached'),
                        variant: 'default',
                    });
                }
            }

            return { updatedFavorites, isAdding, userId };
        },
        onMutate: async ({ questionText, organizationId, interactionId }) => {
            const previousUserInfo = queryClient.getQueryData(['userInfo']);

            const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
            let isAdding = false;

            if (userInfo) {
                const userSettings = userInfo.settings || {};
                const currentSettings = (userSettings[organizationId] as Record<string, unknown>) || {};
                const currentFavorites = (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

                const existingIndex = interactionId
                    ? currentFavorites.findIndex((fav) => fav.interactionId === interactionId)
                    : currentFavorites.findIndex((fav) => fav.title === questionText);

                isAdding = existingIndex === -1;
            }

            queryClient.setQueryData(['userInfo'], (old: IAuthMeV2OutputDto | undefined) => {
                if (!old) return old;

                const userSettings = old.settings || {};
                const currentSettings = (userSettings[organizationId] as Record<string, unknown>) || {};
                const currentFavorites = (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

                const existingIndex = interactionId
                    ? currentFavorites.findIndex((fav) => fav.interactionId === interactionId)
                    : currentFavorites.findIndex((fav) => fav.title === questionText);
                let updatedFavorites: FavoriteQuestion[];

                if (existingIndex !== -1) {
                    updatedFavorites = currentFavorites.filter((_, index) => index !== existingIndex);
                } else {
                    const newFavorite: FavoriteQuestion = {
                        id: interactionId as string,
                        title: questionText,
                        interactionId: interactionId as string,
                    };
                    updatedFavorites = [newFavorite, ...currentFavorites].slice(0, 4);
                }

                return {
                    ...old,
                    settings: {
                        ...userSettings,
                        [organizationId]: {
                            ...currentSettings,
                            'mindy:favorite_questions': updatedFavorites,
                        },
                    },
                };
            });

            return { previousUserInfo, isAdding };
        },
        onSuccess: async (data, variables, context) => {
            const message = context?.isAdding ? t('chat.favorites.added') : t('chat.favorites.removed');

            toast({
                description: message,
                variant: 'default',
            });

            if (data) {
                try {
                    const currentUserInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

                    if (currentUserInfo) {
                        const userSettings = currentUserInfo.settings || {};
                        const currentSettings =
                            (userSettings[variables.organizationId] as Record<string, unknown>) || {};
                        const currentFavorites =
                            (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

                        await updateFavoriteQuestionsMutation.mutateAsync({
                            userId: data.userId,
                            favoriteQuestions: currentFavorites,
                        });
                    }
                } catch (error) {
                    console.error('Error updating favorites on server:', error);
                }
            }
        },
        onError: (error, variables, context) => {
            if (context?.previousUserInfo) {
                queryClient.setQueryData(['userInfo'], context.previousUserInfo);
            }

            toast({
                description: t('chat.favorites.error'),
                variant: 'destructive',
            });
        },
    });
}

export function useIsFavoriteQuestion() {
    const queryClient = useQueryClient();

    return (questionText: string, organizationId: string, interactionId?: string): boolean => {
        const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

        if (!userInfo) {
            return false;
        }

        const userSettings = userInfo.settings || {};
        const currentSettings = (userSettings[organizationId] as Record<string, unknown>) || {};
        const currentFavorites = (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

        if (interactionId) {
            return currentFavorites.some((fav) => fav.interactionId === interactionId);
        }
        return currentFavorites.some((fav) => fav.title === questionText);
    };
}

export function useCombinedSuggestedQuestions(
    organizationId: string,
    defaultQuestions: Array<{ id: string; title: string }>,
    persistedSuggestedQuestions: Array<{ id: string; title: string }> = [],
    options?: {
        includeFavorites?: boolean;
        includeDynamicQuestions?: boolean;
    },
) {
    const queryClient = useQueryClient();
    const includeFavorites = options?.includeFavorites ?? true;
    const includeDynamicQuestions = options?.includeDynamicQuestions ?? true;

    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

    const userSettings = userInfo?.settings || {};
    const currentSettings = (userSettings[organizationId] as Record<string, unknown>) || {};
    const favoriteQuestions = (currentSettings['mindy:favorite_questions'] as FavoriteQuestion[]) || [];

    const formattedFavorites = includeFavorites
        ? favoriteQuestions.map((fav) => ({
              id: fav.id,
              title: fav.title,
              isFavorite: true,
              interactionId: fav.interactionId,
          }))
        : [];

    const combined = [...formattedFavorites];

    const remainingSlots = 4 - combined.length;
    if (includeDynamicQuestions && remainingSlots > 0) {
        const additionalSuggested = persistedSuggestedQuestions.slice(0, remainingSlots).map((q) => ({
            ...q,
            isFavorite: false,
            interactionId: undefined,
        }));
        combined.push(...additionalSuggested);
    }

    const stillRemainingSlots = 4 - combined.length;
    if (stillRemainingSlots > 0) {
        const additionalDefaults = defaultQuestions.slice(0, stillRemainingSlots).map((q) => ({
            ...q,
            isFavorite: false,
            interactionId: undefined,
        }));
        combined.push(...additionalDefaults);
    }

    return combined.slice(0, 4);
}
